// «Qué puede hacer»: the Abilities an agent has and the Skills it applies
// (pantallas-abilities-y-skills, phase 4). It sits under «Qué puede consultar»
// and works the same way: the form keeps the state, this draws it, and saving
// happens with the rest of the agent.
//
// A Skill the agent cannot carry out -it lacks an Ability the Skill needs- is
// shown off and says what is missing, instead of letting it be ticked and then
// failing on save. Turning off an Ability a ticked Skill needs says that Skill
// will stop applying (R6).

import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useAbilitiesQuery, useSkillsQuery } from '@/queries/agents'
import type { Ability, Skill } from '@/types/agent'

interface Props {
  abilityIds: string[]
  skillIds: string[]
  onAbilityIdsChange: (ids: string[]) => void
  onSkillIdsChange: (ids: string[]) => void
  disabled?: boolean
}

function missingFor(skill: Skill, abilityIds: string[], abilities: Ability[]): string[] {
  return skill.ability_ids
    .filter((id) => !abilityIds.includes(id))
    .map((id) => abilities.find((a) => a.id === id)?.name ?? id)
}

export function AgentCapabilities({ abilityIds, skillIds, onAbilityIdsChange, onSkillIdsChange, disabled }: Props) {
  const { t } = useTranslation()
  const { data: abilities = [] } = useAbilitiesQuery()
  const { data: skills = [] } = useSkillsQuery()

  const toggle = (list: string[], id: string, on: boolean) => (on ? [...list, id] : list.filter((x) => x !== id))
  const stopping = skills.filter((s) => skillIds.includes(s.id) && missingFor(s, abilityIds, abilities).length > 0)

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">{t('agent_form.capacidades.abilities')}</p>
        {abilities.map((ability) => (
          <div key={ability.id} className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm">{ability.name}</p>
              {ability.description && <p className="text-xs text-muted-foreground">{ability.description}</p>}
            </div>
            <Switch
              checked={abilityIds.includes(ability.id)}
              onCheckedChange={(on) => onAbilityIdsChange(toggle(abilityIds, ability.id, on))}
              disabled={disabled || !ability.is_enabled}
              aria-label={ability.name}
            />
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">{t('agent_form.capacidades.skills')}</p>
        {skills.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('agent_form.capacidades.sin_skills')}</p>
        ) : (
          skills.map((skill) => {
            const missing = missingFor(skill, abilityIds, abilities)
            const checked = skillIds.includes(skill.id)
            return (
              <div key={skill.id} className="flex items-start gap-2">
                <Checkbox
                  id={`agent-skill-${skill.id}`}
                  checked={checked}
                  disabled={disabled || (!checked && missing.length > 0)}
                  onCheckedChange={(on) => onSkillIdsChange(toggle(skillIds, skill.id, on === true))}
                  aria-label={skill.name}
                />
                <Label htmlFor={`agent-skill-${skill.id}`} className="font-normal">
                  {skill.name}
                  {!checked && missing.length > 0 && (
                    <span className="block text-xs text-muted-foreground">
                      {t('agent_form.capacidades.necesita', { abilities: missing.join(', ') })}
                    </span>
                  )}
                </Label>
              </div>
            )
          })
        )}
      </div>

      {stopping.length > 0 && (
        <div role="status" className="space-y-1 rounded-md bg-warning/10 p-3 text-xs text-warning">
          {stopping.map((skill) => (
            <p key={skill.id}>
              {t('agent_form.capacidades.dejara_de_aplicarse', {
                skill: skill.name,
                abilities: missingFor(skill, abilityIds, abilities).join(', '),
              })}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
